import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import Logo from "@/components/layout/Logo";

const NAV_LINKS = [
  { label: "Solutions", to: "/solutions" },
  { label: "About", to: "/about" },
  { label: "Careers", to: "/careers" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 backdrop-blur border-b transition-colors duration-200 ${
        scrolled
          ? "supports-[backdrop-filter]:bg-[rgba(11,15,20,0.72)] bg-[rgba(11,15,20,0.92)] border-border"
          : "bg-transparent border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-[var(--container-max)] items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />

        <div className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`navbar-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm transition-colors hover:text-foreground ${
                  isActive ? "text-foreground" : "text-muted-foreground"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground">
            <Link to="/contact" data-testid="navbar-contact-button">Contact sales</Link>
          </Button>
          <Button
            asChild
            className="bg-primary text-primary-foreground shadow-[0_0_0_1px_rgba(20,184,166,0.25),0_10px_30px_rgba(20,184,166,0.12)] transition-transform hover:-translate-y-0.5 hover:bg-[hsl(173_80%_34%)]"
          >
            <Link to="/demo" data-testid="navbar-request-demo-button">
              Request a demo
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {/* Mobile */}
        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="navbar-mobile-menu-button" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] border-border bg-background">
              <div className="mt-2 mb-8">
                <Logo onClick={() => setOpen(false)} />
              </div>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <SheetClose asChild key={link.to}>
                    <NavLink
                      to={link.to}
                      data-testid={`navbar-mobile-link-${link.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-3 text-base transition-colors hover:bg-secondary ${
                          isActive ? "text-foreground" : "text-muted-foreground"
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-3">
                <SheetClose asChild>
                  <Button asChild variant="secondary" className="w-full">
                    <Link to="/contact">Contact sales</Link>
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]">
                    <Link to="/demo">Request a demo</Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
